/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { Button, Grid, Backdrop, CircularProgress } from '@mui/material';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import {
  ReactHookFormHiddenInput,
  ReactHookFormTextField,
} from '@opengeoweb/form-fields';
import {
  useConfirmationDialog,
  usePreventBrowserClose,
  CustomTooltip,
} from '@opengeoweb/shared';
import FormWrapper from './FormWrapper';
import LifeCycleEditOptions from './LifeCycleEditOptions';
import type { ConstructRepopulateNotificationType } from './utils';
import {
  constructOutgoingNotification,
  constructRepopulateNotification,
} from './utils';
import type { SWEvent, SWNotification } from '../../types';
import { useSpaceweatherTranslation } from '../../utils/i18n';
import {
  useDiscardDraftNotification,
  usePopulateTemplateContent,
  usePostNotification,
} from '../../utils/spaceweather-api/hooks';

// hidden input helpers, should not be part of send formdata
const HIDDEN_INPUT_HELPER_IS_POPULATE_REQUEST = 'IS_POPULATE_REQUEST';

const useIsPopulateFormHelpers = (): {
  toggleIsPopulate: (isPopulate: boolean) => void;
  isPopulate: () => boolean;
  PopulateFieldHelper: () => React.ReactElement;
} => {
  const { getValues, setValue, register } = useFormContext();

  const isPopulate = (): boolean =>
    getValues(HIDDEN_INPUT_HELPER_IS_POPULATE_REQUEST) === true;

  const toggleIsPopulate = (isPopulate: boolean): void =>
    setValue(HIDDEN_INPUT_HELPER_IS_POPULATE_REQUEST, isPopulate);

  const PopulateFieldHelper = (): React.ReactElement => (
    <input
      defaultValue="false"
      {...(register(
        HIDDEN_INPUT_HELPER_IS_POPULATE_REQUEST,
      ) as unknown as Record<string, unknown>)}
      type="hidden"
    />
  );

  return { toggleIsPopulate, isPopulate, PopulateFieldHelper };
};

const styles = {
  config: {
    width: '1000px',
  },
  optionButton: {
    marginRight: '10px',
  },
  repopulateButton: {
    marginTop: '10px',
  },
  backdrop: {
    zIndex: 1001,
    color: '#fff',
  },
  errorMessage: {
    color: 'red',
  },
  inputField: {
    marginTop: '0px',
    marginBottom: '10px',
  },
};

const isRequiredForSaveStore = (
  value: string,
  isPopulate: () => boolean,
): boolean | string =>
  !isPopulate() && value === '' ? 'This field is required' : true;

interface LifeCycleEditProps {
  toggleDialogOpen: (formSaved?: boolean) => void;
  statusTagContent?: string;
  eventTypeDisabled?: boolean;
  onFormChange?: (hasChanged: boolean) => void;
  actionMode?: string;
  baseNotificationType: string;
  setErrorStoreMessage?: (value: string) => void;
  setErrorRetrievePopulate?: (value: string) => void;
}

export const LifeCycleEditForm: React.FC<LifeCycleEditProps> = ({
  toggleDialogOpen,
  statusTagContent = 'Alert',
  eventTypeDisabled = false,
  onFormChange = (): void => {},
  actionMode = 'none',
  baseNotificationType = '',
  setErrorStoreMessage = (): void => {},
  setErrorRetrievePopulate = (): void => {},
}: LifeCycleEditProps) => {
  const { t } = useSpaceweatherTranslation();
  const confirmDialog = useConfirmationDialog();
  const { handleSubmit, getValues, formState, setValue } = useFormContext();
  const { isPopulate, toggleIsPopulate, PopulateFieldHelper } =
    useIsPopulateFormHelpers();

  const { mutate: postNotification, isPending: isPendingPostNotfication } =
    usePostNotification();
  const { mutate: discardDraftNotification, isPending: isPendingDiscardDraft } =
    useDiscardDraftNotification();

  const {
    mutate: populateTemplateContent,
    isPending: isPendingPopulateTemplate,
  } = usePopulateTemplateContent();

  const { dirtyFields } = formState;
  const isDirty = Object.keys(dirtyFields).length > 0;

  const hiddenInputFields = [
    'changestateto',
    'draft',
    'originator',
    'notificationid',
    'eventid',
    'issuetime',
  ];

  usePreventBrowserClose(isDirty);

  React.useEffect(() => {
    onFormChange(isDirty);
  }, [isDirty, onFormChange]);

  const handleDiscard = (): void => {
    // If it is a draft that is discarded, show a confirmation message that it will be remove from database
    if (getValues('draft') && getValues('notificationid')) {
      void confirmDialog({
        title: t('discard-dialog-title'),
        description: t('discard-dialog-description'),
        confirmLabel: t('notification-discard'),
        cancelLabel: t('dialog-cancel'),
      }).then(() => {
        discardDraftNotification({
          data: {
            notificationid: getValues('notificationid'),
            eventid: getValues('eventid'),
          },
          setError: setErrorStoreMessage,
          toggleDialogOpen,
        });
      });
    } else {
      // Not a draft, close the dialog - in case of changes, show message that these will be lost if not saved
      toggleDialogOpen();
    }
  };

  const handleStoreNotification = (isDraft = false): void => {
    toggleIsPopulate(false);
    void handleSubmit((data) => {
      postNotification({
        data: constructOutgoingNotification(
          data as SWNotification,
          statusTagContent,
          isDraft,
        ),
        setError: setErrorStoreMessage,
        toggleDialogOpen,
      });
    })();
  };

  const handleRequestRepopulate = (): void => {
    toggleIsPopulate(true);
    void handleSubmit(async (data) => {
      populateTemplateContent(
        {
          data: constructRepopulateNotification(
            data as ConstructRepopulateNotificationType,
            actionMode,
          ),
          setValue,
          setError: setErrorRetrievePopulate,
        },
        { onSettled: () => toggleIsPopulate(false) },
      );
    })();
  };

  // On first load, try (if there are no validation errors) to immediately retrieve the template
  React.useEffect(() => {
    if (baseNotificationType !== 'draft' && baseNotificationType !== 'new') {
      handleRequestRepopulate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Grid container data-testid="edit-lifecycle" spacing={1}>
      <Grid size={12}>
        <LifeCycleEditOptions
          actionMode={actionMode}
          eventTypeDisabled={eventTypeDisabled}
          statusTagContent={statusTagContent}
        />
      </Grid>
      <Grid container size={12} spacing={1}>
        <Grid size={9}>
          <ReactHookFormTextField
            label={t('notification-event-title')}
            name="title"
            rules={{
              validate: {
                isRequiredForSaveStore: (value): boolean | string =>
                  isRequiredForSaveStore(value, isPopulate),
              },
            }}
            slotProps={{ htmlInput: { 'data-testid': 'notification-title' } }}
            sx={styles.inputField}
          />
        </Grid>
        <Grid size={3}>
          <CustomTooltip
            placement="top"
            style={{ verticalAlign: 'baseLine' }}
            title={t('notification-populate-title')}
          >
            <Button
              color="secondary"
              data-testid="notification-repopulate"
              onClick={(): void => {
                handleRequestRepopulate();
              }}
              sx={styles.repopulateButton}
              variant="outlined"
            >
              {t('notification-populate')}
            </Button>
          </CustomTooltip>
        </Grid>
      </Grid>
      <Grid size={12}>
        <ReactHookFormTextField
          multiline
          name="message"
          placeholder={t('notification-event-description')}
          rows={20}
          rules={{
            validate: {
              isRequiredForSaveStore: (value): boolean | string =>
                isRequiredForSaveStore(value, isPopulate),
            },
          }}
          slotProps={{ htmlInput: { 'data-testid': 'notification-text' } }}
          sx={{
            backgroundColor: 'rgba(0, 117, 169, 0.05)',
          }}
          variant="outlined"
        />
      </Grid>
      <PopulateFieldHelper />
      <Grid
        container
        data-testid="new-notification-options"
        size={12}
        spacing={0}
        sx={{
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        <Grid>
          <Button
            color="secondary"
            data-testid="discard"
            onClick={handleDiscard}
            sx={styles.optionButton}
          >
            {t('notification-discard')}
          </Button>
        </Grid>
        <Grid>
          <Button
            color="secondary"
            data-testid="draft"
            onClick={(): void => {
              handleStoreNotification(true);
            }}
            sx={styles.optionButton}
            variant="outlined"
          >
            {t('notification-save')}
          </Button>
        </Grid>
        <Grid>
          <Button
            color="secondary"
            data-testid="issue"
            onClick={(): void => {
              handleStoreNotification();
            }}
            sx={styles.optionButton}
            variant="contained"
          >
            {t('notification-issue')}
          </Button>
        </Grid>
      </Grid>
      <Backdrop
        data-testid="loader"
        open={
          isPendingPostNotfication ||
          isPendingDiscardDraft ||
          isPendingPopulateTemplate
        }
        sx={styles.backdrop}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      {hiddenInputFields.map((field) => (
        <ReactHookFormHiddenInput key={field} name={field} />
      ))}
    </Grid>
  );
};

export interface WrapperProps extends LifeCycleEditProps {
  baseNotificationData?: SWEvent;
  baseNotificationType: string;
}

const LifeCycleEdit: React.FC<WrapperProps> = ({
  baseNotificationData,
  baseNotificationType,
  ...otherProps
}: WrapperProps) => {
  return (
    <FormWrapper
      baseNotificationData={baseNotificationData}
      baseNotificationType={baseNotificationType}
    >
      <LifeCycleEditForm
        {...otherProps}
        baseNotificationType={baseNotificationType}
      />
    </FormWrapper>
  );
};

export default LifeCycleEdit;
